/**
 * @file DropdownSearchableSingleSelect.js
 * @description Single select dropdown with searchable functionality
 */

import { useState, useEffect, useRef } from 'react';
import { useWindowListener } from '../helpers/useWindowListener';
import '../helpers/Dropdown.css';
import { v4 as uuidv4 } from 'uuid';
import { getText } from '../../i18n';

const resolveDefaultValue = value =>
    typeof value === 'function' ? value() : value;

export function Dropdown({
    allowSearch = false,
    defaultValue,
    options,
    onChange,
    disabled = false,
    isTitleEnabled = false,
    titleLabel = '',
    isBottomOptionEnabled = false,
    bottomOptionLabel = '',
    onBottomOptionClick,
    searchBarClassName = '',
    useStandardFormStyle = false,
}) {
    const [showOptionList, setShowOptionList] = useState(false);
    const resolvedDefaultValue = resolveDefaultValue(defaultValue) ?? '';
    const [selectedText, setSelectedText] = useState(resolvedDefaultValue);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const componentRef = useRef(null);

    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    useEffect(() => {
        const nextDefaultValue = resolveDefaultValue(defaultValue) ?? '';

        if (!nextDefaultValue) {
            const newValue = options[0]?.label ?? '';
            setSelectedText(newValue);
            onChange(newValue);
        } else {
            setSelectedText(nextDefaultValue);
        }
    }, [options, defaultValue, onChange]);

    useWindowListener('mousedown', event => {
        if (
            componentRef.current &&
            !componentRef.current.contains(event.target)
        ) {
            setShowOptionList(false);
            setSearchTerm('');
            setFilteredOptions(options);
        }
    });

    const toggleShowOptionList = event => {
        event.preventDefault();
        if (!disabled) setShowOptionList(!showOptionList);
    };

    const handleOptionSelect = option => {
        setSelectedText(option.label);
        setShowOptionList(false);
        setSearchTerm('');
        setFilteredOptions(options);
        onChange(option.value);
    };

    const handleSearchChange = event => {
        const term = event.target.value;
        setSearchTerm(term);
        const filtered = options.filter(option =>
            option.label.toLowerCase().includes(term.toLowerCase()),
        );
        setFilteredOptions(filtered);
    };

    const displayValue = disabled ? resolvedDefaultValue : selectedText;
    const isPlaceholderValue =
        useStandardFormStyle && displayValue === 'Select';

    return (
        <div className="relative-position" ref={componentRef}>
            <div
                className={
                    'dropdownBox selectedText' +
                    (showOptionList ? ' active' : '') +
                    (disabled ? ' disabled' : '')
                }
                onClick={toggleShowOptionList}
            >
                <div
                    className={`dropdownLabel${isPlaceholderValue ? ' dropdownLabelPlaceholder' : ''}`}
                >
                    {displayValue}
                </div>
                <i
                    className={`bi bi-chevron-${showOptionList ? 'up' : 'down'}`}
                />
            </div>

            {showOptionList && (
                <ul
                    className="selectList"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        maxHeight: '300px',
                        zIndex: 1000,
                    }}
                >
                    {isTitleEnabled && (
                        <p className="dropdownTitle">{titleLabel}</p>
                    )}

                    {allowSearch && (
                        <>
                            <div
                                className={`dropdownSearchBar${searchBarClassName ? ` ${searchBarClassName}` : ''}${useStandardFormStyle ? ' dropdownSearchBarStandard' : ''}`}
                            >
                                <i className="bi bi-search dropdownSearchIcon" />
                                <input
                                    className="searchBar"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    type="text"
                                    placeholder={getText('msg_5616c8722256')}
                                    onClick={e => e.stopPropagation()}
                                />
                            </div>
                            <hr className="dropdownDivider my-2" />
                        </>
                    )}

                    <div className="mb-2">
                        {filteredOptions.map(option => (
                            <li
                                className="selectOption"
                                key={uuidv4()}
                                onClick={() => handleOptionSelect(option)}
                            >
                                {option.label}
                            </li>
                        ))}
                    </div>

                    {isBottomOptionEnabled && (
                        <>
                            <hr className="dropdownDivider mb-2" />
                            <p
                                className="bottomOption"
                                onClick={() => {
                                    onBottomOptionClick();
                                    setShowOptionList(false);
                                    setSearchTerm('');
                                    setFilteredOptions(options);
                                }}
                            >
                                {bottomOptionLabel}
                            </p>
                        </>
                    )}
                </ul>
            )}
        </div>
    );
}
